/* Kocktail — AJAX add-to-cart for the custom buy box.

   Our PDP uses a plain {% form 'product' %} (so it still works with JS off).
   This module upgrades any form marked `data-kx-cart-form` to add via fetch and
   open Horizon's cart drawer, exactly like the theme's own product form — it
   dispatches the same `CartLinesUpdateEvent` the drawer / cart-icon / cart-items
   components listen for.

   It also supports an optional gift-box add-on: a checkbox marked
   `data-kx-gift-check` inside a `[data-kx-gift-variant]` label adds that variant
   as its own cart line (so it is charged at its real price).

   Safety: if Horizon's event module isn't available we DON'T intercept, so the
   native form POST still works and add-to-cart is never broken. */
import { CartLinesUpdateEvent, CartErrorEvent } from '@shopify/events';

const USABLE =
  typeof CartLinesUpdateEvent === 'function' &&
  typeof CartLinesUpdateEvent.createPromise === 'function';

// `Theme` is declared with `const` in snippets/scripts.liquid, so it is a global
// lexical binding rather than a property of `window` — `window.Theme` is always
// undefined and testing it silently forced the hardcoded fallbacks, ignoring any
// market/locale URL prefix. Vendor scripts (product-form.js, cart-note.js) read
// the bare identifier, so match them.
const ROUTES = (typeof Theme !== 'undefined' && Theme.routes) || {};
const ADD_URL = ROUTES.cart_add_url || '/cart/add.js';
const CART_URL = ROUTES.cart_url || '/cart';

function sectionIds() {
  const ids = [];
  document.querySelectorAll('cart-items-component').forEach(function (el) {
    var id = el.dataset.sectionId;
    if (id && ids.indexOf(id) === -1) ids.push(id);
  });
  return ids;
}

function refreshCart() {
  return fetch(CART_URL + '.js', { headers: { Accept: 'application/json' } }).then(function (r) {
    return r.json();
  });
}

// Belt-and-suspenders: open the cart drawer directly, independent of the
// auto-open event path. The theme's <theme-drawer id="cart-drawer"> exposes
// open()/isOpen once upgraded. No-op on cart-page templates (no drawer).
function openCartDrawer() {
  var d = document.getElementById('cart-drawer');
  if (d && typeof d.open === 'function' && !d.isOpen) {
    try { d.open(); } catch (e) {}
  }
  nudgeDrawerRecs();
}

// Load the drawer "Add to your order" recommendations by fetching the
// pre-rendered cart-drawer-upsell section and injecting it directly. This is
// deterministic inside the dialog — unlike the <product-recommendations> web
// component, whose IntersectionObserver is unreliable below the drawer fold and
// which aborted its own fetch when the open-nudge re-triggered it. We fetch once
// per product id (data-kx-recs-for) so opening/closing never re-fetches.
function nudgeDrawerRecs() {
  document.querySelectorAll('[data-kx-drawer-recs]').forEach(function (el) {
    var pid = el.getAttribute('data-product-id');
    if (!pid) return;
    if (el.getAttribute('data-kx-recs-for') === pid && el.children.length > 0) return; // loaded & still present
    el.setAttribute('data-kx-recs-for', pid);
    var url = el.getAttribute('data-url') + '&product_id=' + encodeURIComponent(pid);
    fetch(url, { headers: { Accept: 'text/html' } })
      .then(function (r) { return r.ok ? r.text() : ''; })
      .then(function (html) { if (html && html.trim()) { el.innerHTML = html; syncUpsellNav(el); } })
      .catch(function () { el.removeAttribute('data-kx-recs-for'); });
  });
}

// Upsell carousel arrows: toggle is-start/is-end on the .kx-up container so the
// prev/next arrows dim at the ends. Runs on inject, on click and on scroll.
function updateUpsellNav(row) {
  var wrap = row.closest ? row.closest('[data-kx-up]') : null;
  if (!wrap) return;
  var max = row.scrollWidth - row.clientWidth;
  wrap.classList.toggle('is-start', row.scrollLeft <= 1);
  wrap.classList.toggle('is-end', row.scrollLeft >= max - 1);
}
function syncUpsellNav(scope) {
  (scope || document).querySelectorAll('[data-kx-up-row]').forEach(updateUpsellNav);
}

function buildItems(form) {
  var fd = new FormData(form);
  var props = {};
  fd.forEach(function (value, key) {
    var m = key.match(/^properties\[(.+)\]$/);
    if (m) props[m[1]] = value;
  });

  var main = { id: Number(fd.get('id')), quantity: Number(fd.get('quantity')) || 1 };
  if (Object.keys(props).length) main.properties = props;
  var items = [main];

  // Optional gift-box product line
  var giftLabel = form.querySelector('[data-kx-gift-variant]');
  if (giftLabel) {
    var check = giftLabel.querySelector('input[type="checkbox"]');
    if (check && check.checked) {
      items.push({ id: Number(giftLabel.getAttribute('data-kx-gift-variant')), quantity: 1 });
    }
  }
  return items;
}

function enhance(form) {
  form.addEventListener('submit', function (event) {
    event.preventDefault();

    var btn = form.querySelector('[type="submit"]');
    // A second tap while the first add is still in flight would POST twice
    // and double the quantity (gift box included).
    if (btn && btn.getAttribute('aria-busy') === 'true') return;
    var errEl = form.querySelector('[data-kx-add-err]');
    if (errEl) { errEl.hidden = true; errEl.textContent = ''; }
    if (btn) btn.setAttribute('aria-busy', 'true');

    var items = buildItems(form);
    var itemCount = items.reduce(function (n, it) { return n + it.quantity; }, 0);
    var deferred = CartLinesUpdateEvent.createPromise();

    // Announce the add up-front (drawer opens, components await the promise).
    form.dispatchEvent(
      new CartLinesUpdateEvent({
        action: 'add',
        context: 'product',
        lines: items.map(function (it) {
          return { merchandiseId: String(it.id), quantity: it.quantity };
        }),
        promise: deferred.promise,
      })
    );

    fetch(ADD_URL, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
      body: JSON.stringify({ items: items, sections: sectionIds().join(',') }),
    })
      .then(function (r) { return r.json().then(function (data) { return { ok: r.ok, data: data }; }); })
      .then(function (res) {
        if (!res.ok || res.data.status) {
          form.dispatchEvent(
            new CartErrorEvent({
              error: res.data.message || 'Add to cart failed',
              code: 'INVALID',
              detail: { description: res.data.description, errors: res.data.errors },
            })
          );
          // Nothing listens globally for CartErrorEvent, so without this line
          // a rejected add (e.g. Shopify's stock cap) is invisible: the
          // spinner just stops and the button looks broken.
          if (errEl) {
            errEl.textContent = res.data.description || res.data.message || "Sorry — that couldn't be added. Please try again.";
            errEl.hidden = false;
          }
          deferred.reject(new Error(res.data.message || 'add failed'));
          return;
        }
        return refreshCart().then(function (ajaxCart) {
          deferred.resolve({
            cart: CartLinesUpdateEvent.createCartFromAjaxResponse(ajaxCart),
            detail: {
              items: ajaxCart.items,
              source: 'kocktail-cart-add',
              itemCount: itemCount,
              sections: res.data.sections,
              didError: false,
            },
          });
          openCartDrawer();
        });
      })
      .catch(function (err) {
        deferred.reject(err);
        form.dispatchEvent(
          new CartErrorEvent({ error: (err && err.message) || 'Network error', code: 'SERVICE_UNAVAILABLE' })
        );
        // This path also fires if the add landed but the follow-up cart
        // refresh dropped, so don't assert failure or invite a retry.
        if (errEl) {
          errEl.textContent = 'Something went wrong — please check your basket.';
          errEl.hidden = false;
        }
      })
      .finally(function () {
        if (btn) btn.removeAttribute('aria-busy');
      });
  });
}

// Add arbitrary items and refresh the (already-open) cart drawer. Used by the
// drawer upsell buttons — plain buttons, not a nested product form, so the cart
// form stays stable.
function addAndRefresh(items, btn) {
  var deferred = CartLinesUpdateEvent.createPromise();
  document.dispatchEvent(
    new CartLinesUpdateEvent({
      action: 'add',
      context: 'cart',
      lines: items.map(function (it) { return { merchandiseId: String(it.id), quantity: it.quantity }; }),
      promise: deferred.promise,
    })
  );
  // One request per line, in order. Apps that decorate a line as it is added
  // — pre-order, subscriptions, bundling — hook /cart/add.js and read the
  // single-item shape. A three-bottle bundle sent as one {items:[...]} call is
  // a shape many of them only half-read: the first entry is processed and the
  // rest go past untouched, which is what a bundle coming back with one
  // pre-order line looks like. The sections payload rides on the last request,
  // since that is the one whose rendered cart is used.
  var chain = items
    .reduce(function (chain, item, i) {
      return chain.then(function () {
        var body = { id: item.id, quantity: item.quantity };
        if (item.properties) body.properties = item.properties;
        if (i === items.length - 1) body.sections = sectionIds().join(',');
        return fetch(ADD_URL, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
          body: JSON.stringify(body),
        })
          .then(function (r) { return r.json().then(function (d) { return { ok: r.ok, data: d }; }); })
          .then(function (res) {
            if (!res.ok || res.data.status) throw new Error(res.data.message || 'add failed');
            return res;
          });
      });
    }, Promise.resolve())
    .then(function (res) {
      return refreshCart().then(function (ajaxCart) {
        deferred.resolve({
          cart: CartLinesUpdateEvent.createCartFromAjaxResponse(ajaxCart),
          detail: { items: ajaxCart.items, source: 'kocktail-upsell', sections: res && res.data.sections, didError: false },
        });
        openCartDrawer();
      });
    })
    .then(
      function () { return true; },
      function (err) { deferred.reject(err); return false; }
    )
    .finally(function () { if (btn) btn.removeAttribute('aria-busy'); });

  // Resolves true when every line landed, false when one did not. It never
  // rejects: callers that only want the drawer opened (the upsell buttons)
  // ignore the result, and an unhandled rejection for them would be noise.
  return chain;
}

// Public hook so other Kocktail scripts (e.g. the bundle "Buy as" flow) can add
// items and open the drawer instead of doing a full navigation. Returns a
// promise of true/false so the caller can reset or keep its own button state.
window.KocktailCart = {
  add: function (items) {
    return USABLE ? addAndRefresh(items, null) : Promise.resolve(false);
  },
  openDrawer: openCartDrawer,
  usable: USABLE,

  // Tell the drawer a line was added or removed by something outside this
  // module — the gift note, which is a classic script so that a failure here
  // cannot take it down with us. Only offered when the events module actually
  // loaded; the caller re-renders the cart section itself when it is not.
  announce: USABLE
    ? function (ajaxCart, action, variantId) {
        var deferred = CartLinesUpdateEvent.createPromise();
        document.dispatchEvent(
          new CartLinesUpdateEvent({
            action: action === 'remove' ? 'remove' : 'add',
            context: 'cart',
            lines: [{ merchandiseId: String(variantId), quantity: action === 'remove' ? 0 : 1 }],
            promise: deferred.promise,
          })
        );
        deferred.resolve({
          cart: CartLinesUpdateEvent.createCartFromAjaxResponse(ajaxCart),
          detail: { items: ajaxCart.items, source: 'kocktail-gift-note', didError: false },
        });
        return Promise.resolve();
      }
    : undefined,
};

function init() {
  // Only the product-form interception depends on Horizon's event module: if
  // it is missing we must NOT preventDefault, so the native POST still works
  // and add-to-cart is never broken.
  //
  // Everything below used to sit behind the same early return, which meant one
  // missing import took the gift note, the upsell buttons and the click-away
  // close with it — none of which need that module. They announce through it
  // when it is there and simply refresh the cart when it is not.
  if (USABLE) document.querySelectorAll('form[data-kx-cart-form]').forEach(enhance);

  // Click away to close the cart drawer on desktop.
  //
  // Horizon runs the drawer as a MODAL below 990px and as a non-modal sidebar
  // above it. Its own dismiss handler is bound to the dialog and relies on the
  // backdrop: in modal mode a click outside lands on the dialog itself and
  // closes it, but a non-modal dialog has no backdrop, so an outside click
  // lands on the page and the drawer never hears about it. That is the theme's
  // intent — the sidebar squeezes the page rather than covering it — but a
  // basket that will not close when you click back at the page reads as stuck.
  //
  // pointerdown, not click, so the decision is made against the element that
  // was actually under the cursor rather than whatever survives a re-render.
  document.addEventListener(
    'pointerdown',
    function (e) {
      var drawer = document.getElementById('cart-drawer');
      if (!drawer || !drawer.isOpen) return;
      if (!window.matchMedia('(min-width: 990px)').matches) return;
      var t = e.target;
      if (!(t instanceof Element)) return;
      if (drawer.contains(t)) return;
      // The basket button is a toggle. Closing here as well would race it —
      // close, then toggle, and the drawer reopens under the user's hand.
      if (t.closest('[aria-controls="cart-drawer"], [data-testid="cart-drawer-trigger"]')) return;
      // Anything modal sitting on top owns the click.
      if (t.closest('dialog[open]')) return;
      try { drawer.close(); } catch (err) {}
    },
    true
  );

  // The drawer re-renders (morphs) on every cart change, which wipes any injected
  // recommendations — "they show for a second and disappear". Observe the drawer
  // and re-inject after each morph; also load once now for carts that already
  // have items so they're ready before the drawer is even opened. nudgeDrawerRecs
  // is idempotent (skips when the cards are already present for the current item).
  var cartDrawerEl = document.getElementById('cart-drawer');
  if (cartDrawerEl) {
    var recsScheduled = false;
    new MutationObserver(function () {
      if (recsScheduled) return;
      recsScheduled = true;
      requestAnimationFrame(function () { recsScheduled = false; nudgeDrawerRecs(); });
    }).observe(cartDrawerEl, { childList: true, subtree: true });
    nudgeDrawerRecs();
  }

  // Delegated so it works for upsell items injected into the drawer at any time.
  document.addEventListener('click', function (e) {
    var btn = e.target && e.target.closest ? e.target.closest('[data-kx-upsell-add]') : null;
    if (!btn || btn.hasAttribute('disabled') || btn.getAttribute('aria-busy') === 'true') return;
    var vid = btn.getAttribute('data-variant-id');
    if (!vid) return;
    btn.setAttribute('aria-busy', 'true');
    addAndRefresh([{ id: Number(vid), quantity: 1 }], btn);
  });

  // Upsell prev/next arrows — scroll the recommendations row by roughly one card.
  document.addEventListener('click', function (e) {
    var nav = e.target && e.target.closest ? e.target.closest('[data-kx-up-nav]') : null;
    if (!nav) return;
    var wrap = nav.closest('[data-kx-up]');
    var row = wrap && wrap.querySelector('[data-kx-up-row]');
    if (!row) return;
    var card = row.querySelector('.kx-up__card');
    var step = card ? card.offsetWidth + 12 : Math.round(row.clientWidth * 0.8);
    row.scrollBy({ left: nav.getAttribute('data-kx-up-nav') === 'prev' ? -step : step, behavior: 'smooth' });
  });

  // Keep arrow end-states in sync while the row scrolls (capture: scroll doesn't bubble).
  document.addEventListener('scroll', function (e) {
    var row = e.target && e.target.matches && e.target.matches('[data-kx-up-row]') ? e.target : null;
    if (row) updateUpsellNav(row);
  }, true);

}

if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', init);
} else {
  init();
}
